export interface HotelSubscriptionAccessInput {
  /** `?tenantId=` — platform entry from TenantEditPage. */
  tenantId: string | undefined;
  /** `platform:subscriptions:read` (administrate implies read). */
  canReadPlatformSubscriptions: boolean;
  canAdminHotelBrands: boolean;
  isTenantAdmin: boolean;
  canManageBilling: boolean;
}

/**
 * Who may open the hotel plan dialog. In tenant mode (`?tenantId=`) the
 * platform admin needs `platform:subscriptions:read` (viewing; assigning needs
 * administrate, see `canAssignHotelPlans`); otherwise the tenant admin rules
 * apply.
 */
export function canManageHotelSubscriptions({
  tenantId,
  canReadPlatformSubscriptions,
  canAdminHotelBrands,
  isTenantAdmin,
  canManageBilling,
}: HotelSubscriptionAccessInput): boolean {
  if (tenantId) {
    return canReadPlatformSubscriptions;
  }

  return canAdminHotelBrands && isTenantAdmin && canManageBilling;
}

export interface AssignHotelPlansInput {
  /** `?tenantId=` — platform entry from TenantEditPage. */
  tenantId: string | undefined;
  canAdministerPlatformSubscriptions: boolean;
  isPlatformAdmin: boolean;
}

/**
 * Who sees Assign plan / Unassign. Only SUPER_ADMIN and GUESTERIA_ADMIN
 * (owner decision, DV-13) and only in tenant mode, where `x-tenant-id` names
 * the tenant. Tenant roles (TENANT_ADMIN, HOTEL_ADMIN, FRONT_DESK, STAFF,
 * GUEST) only view the subscription.
 */
export function canAssignHotelPlans({
  tenantId,
  canAdministerPlatformSubscriptions,
  isPlatformAdmin,
}: AssignHotelPlansInput): boolean {
  return (
    isPlatformAdmin && Boolean(tenantId) && canAdministerPlatformSubscriptions
  );
}

export interface PlanActionVisibilityInput {
  canAssign: boolean;
  canManage: boolean;
  isCurrent: boolean;
  paymentRequired: boolean;
}

/**
 * Buttons of a plan card: the primary one is Pay now (for whoever manages the
 * tenant billing) or Assign plan (assign right only); Unassign needs the
 * assign right and is shown on the current plan only (DV-168: the platform
 * unassigns in tenant mode).
 */
export function getPlanActionVisibility({
  canAssign,
  canManage,
  isCurrent,
  paymentRequired,
}: PlanActionVisibilityInput): { showPrimary: boolean; showRemove: boolean } {
  return {
    showPrimary: paymentRequired ? canManage : canAssign,
    showRemove: canAssign && isCurrent,
  };
}

/** Plans list is loaded for exactly those who may open the dialog. */
export function canLoadHotelSubscriptionPlans(
  input: HotelSubscriptionAccessInput,
): boolean {
  if (input.tenantId) {
    return input.canReadPlatformSubscriptions;
  }

  return input.isTenantAdmin;
}

/**
 * Pay now and feature toggles belong to the tenant: they are not offered to
 * the platform in tenant mode. Assigning and unassigning are the platform's
 * (`canAssignHotelPlans`).
 */
export function canMutateSubscriptionExtras(
  tenantId: string | undefined,
): boolean {
  return !tenantId;
}

/** Request options carrying `x-tenant-id` in tenant mode, nothing otherwise. */
export function tenantHeadersOptions(
  tenantId: string | undefined,
): { headers: Record<string, string> } | undefined {
  return tenantId ? { headers: { "x-tenant-id": tenantId } } : undefined;
}

export interface HotelBrandActionsInput {
  /** `?tenantId=` — platform entry from TenantEditPage. */
  tenantId: string | undefined;
  isPlatformAdmin: boolean;
  /** `platform:tenants:administrate`. */
  canAdministerPlatformTenants: boolean;
  /** `platform:subscriptions:administrate`. */
  canAdministerPlatformSubscriptions: boolean;
  /** Tenant permission `hotel_brands:administrate`. */
  canAdminHotelBrands: boolean;
}

export interface HotelBrandActions {
  canAdd: boolean;
  canEdit: boolean;
  canDelete: boolean;
  canCopy: boolean;
}

/**
 * Hotel Brands actions (owner decision 2026-10-07, DV-168): hotels are
 * created and deleted by platform admins in tenant mode (`x-tenant-id`); tenant
 * roles only edit their hotels. A copy creates a hotel and its plan, so it
 * needs `platform:tenants:administrate` and
 * `platform:subscriptions:administrate`: SUPER_ADMIN, or GUESTERIA_ADMIN in its
 * area (the backend checks the area).
 */
export function getHotelBrandActions({
  tenantId,
  isPlatformAdmin,
  canAdministerPlatformTenants,
  canAdministerPlatformSubscriptions,
  canAdminHotelBrands,
}: HotelBrandActionsInput): HotelBrandActions {
  if (tenantId) {
    const canAdminister = isPlatformAdmin && canAdministerPlatformTenants;

    return {
      canAdd: canAdminister,
      canEdit: canAdminister,
      canDelete: canAdminister,
      canCopy: canAdminister && canAdministerPlatformSubscriptions,
    };
  }

  return {
    canAdd: false,
    canEdit: canAdminHotelBrands,
    canDelete: false,
    canCopy: false,
  };
}
